import React, { useEffect, useState } from 'react';
import API from '../utils/api';
import { toast } from 'react-toastify';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

const EMPTY_FORM = { title: '', description: '', icon: '', order: 0 };

export default function ManageSiteContent() {
  // Hero / About text (stored on Settings)
  const [settings, setSettings] = useState({});
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  // Why Goshala cards
  const [cards, setCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [iconFile, setIconFile] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchSettings = () => {
    setLoadingSettings(true);
    API.get('/settings/admin').then(r => { setSettings(r.data || {}); setLoadingSettings(false); }).catch(() => setLoadingSettings(false));
  };
  const fetchCards = () => {
    setLoadingCards(true);
    API.get('/why-cards/admin').then(r => { setCards(r.data); setLoadingCards(false); }).catch(() => setLoadingCards(false));
  };
  useEffect(() => { fetchSettings(); fetchCards(); }, []);

  const updateSettingField = (field, value) => setSettings(p => ({ ...p, [field]: value }));

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const fd = new FormData();
      fd.append('heroTitle', settings.heroTitle ?? '');
      fd.append('heroSubtitle', settings.heroSubtitle ?? '');
      fd.append('aboutText', settings.aboutText ?? '');
      await API.put('/settings', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Homepage text saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    }
    setSavingSettings(false);
  };

  const resetForm = () => { setForm(EMPTY_FORM); setIconFile(null); setEditingId(null); };
  const openAddForm = () => { resetForm(); setShowForm(true); };
  const openEditForm = (c) => {
    setEditingId(c._id);
    setForm({ title: c.title, description: c.description, icon: c.icon || '', order: c.order || 0 });
    setIconFile(null);
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); resetForm(); };

  const iconIsImage = icon => !!icon && (icon.startsWith('/uploads/') || icon.startsWith('http'));
  const iconUrl = icon => iconIsImage(icon) ? (icon.startsWith('http') ? icon : `${API_BASE}${icon}`) : null;

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    if (!form.description.trim()) return toast.error('Description is required');
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'icon') return;
        fd.append(k, v);
      });
      if (iconFile) fd.append('iconImage', iconFile);
      else fd.append('icon', form.icon);
      if (editingId) {
        await API.put(`/why-cards/${editingId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Card updated');
      } else {
        await API.post('/why-cards', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Card added');
      }
      closeForm();
      fetchCards();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving');
    }
    setSaving(false);
  };

  const handleTogglePublish = async (c) => {
    try {
      await API.put(`/why-cards/${c._id}`, { isActive: !c.isActive });
      toast.success(c.isActive ? 'Moved to draft' : 'Published');
      fetchCards();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Permanently delete this card? This cannot be undone.')) return;
    try {
      await API.delete(`/why-cards/${id}`);
      toast.success('Deleted');
      fetchCards();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error deleting');
    }
  };

  return (
    <div>
      <h2 className="admin-page-title">🖼️ Manage Site Content</h2>
      <p style={{ color: '#888', fontSize: '0.85rem', marginTop: -16, marginBottom: 24 }}>
        Edit the homepage's hero text, About Us text, and the "Why Goshala?" cards from one place.
      </p>

      {/* Hero / About text */}
      <div className="admin-form-card" style={{ marginBottom: 24 }}>
        <h3 style={{ fontWeight: 700, marginBottom: 16 }}>🏠 Hero &amp; About Text</h3>
        {loadingSettings ? <div className="loader"><div className="spinner" /></div> : (
          <>
            <div className="form-group">
              <label className="form-label">Hero Title</label>
              <input className="form-input" value={settings.heroTitle || ''} onChange={e => updateSettingField('heroTitle', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Hero Subtitle</label>
              <input className="form-input" value={settings.heroSubtitle || ''} onChange={e => updateSettingField('heroSubtitle', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">About Us Text</label>
              <textarea className="form-input" rows={5} value={settings.aboutText || ''} onChange={e => updateSettingField('aboutText', e.target.value)} placeholder="Describe your Goshala..." />
            </div>
            <button className="btn btn-primary" onClick={handleSaveSettings} disabled={savingSettings}>
              {savingSettings ? 'Saving...' : 'Save Text'}
            </button>
          </>
        )}
      </div>

      {/* Why Goshala cards */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ fontWeight: 700, margin: 0 }}>🙏 "Why Goshala?" Cards</h3>
        <button className="btn btn-primary" onClick={() => (showForm ? closeForm() : openAddForm())}>
          {showForm ? 'Cancel' : '+ Add Card'}
        </button>
      </div>
      <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: 20 }}>
        The four cards under "Why Goshala?" on the homepage — icon (or a custom uploaded image), title, and description are all editable here.
      </p>

      {showForm && (
        <div className="admin-form-card" style={{ marginBottom: 24 }}>
          <h3 style={{ fontWeight: 700, marginBottom: 16 }}>{editingId ? 'Edit Card' : 'Add Card'}</h3>
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input className="form-input" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Description *</label>
            <textarea className="form-input" rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Icon (emoji or symbol)</label>
              <input className="form-input" placeholder="🐂" value={form.icon} onChange={e => setForm(p => ({ ...p, icon: e.target.value }))} disabled={!!iconFile} />
              <div style={{ fontSize: '0.78rem', color: '#888', marginTop: 4 }}>Used only if no custom image is uploaded below.</div>
            </div>
            <div className="form-group">
              <label className="form-label">Custom Icon Image (optional)</label>
              {iconIsImage(form.icon) && !iconFile && (
                <div style={{ marginBottom: 8 }}>
                  <img src={iconUrl(form.icon)} alt="Current icon" style={{ width: 40, height: 40, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 6 }} />
                </div>
              )}
              <input type="file" accept="image/*" onChange={e => setIconFile(e.target.files[0])} />
            </div>
          </div>
          <div className="form-group" style={{ maxWidth: 160 }}>
            <label className="form-label">Display Order</label>
            <input className="form-input" type="number" value={form.order} onChange={e => setForm(p => ({ ...p, order: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Update' : 'Save'}
            </button>
            <button className="btn btn-outline" onClick={closeForm}>Cancel</button>
          </div>
        </div>
      )}

      {loadingCards ? <div className="loader"><div className="spinner" /></div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
          {cards.map(c => (
            <div key={c._id} style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', position: 'relative', textAlign: 'center', padding: '20px 14px 14px', opacity: c.isActive ? 1 : 0.6 }}>
              <span className={`badge ${c.isActive ? 'badge-green' : 'badge-orange'}`} style={{ position: 'absolute', top: 10, left: 10 }}>
                {c.isActive ? 'Published' : 'Draft'}
              </span>
              <div style={{ marginTop: 20, fontSize: '2rem', marginBottom: 8 }}>
                {iconUrl(c.icon) ? <img src={iconUrl(c.icon)} alt={c.title} style={{ width: 40, height: 40, objectFit: 'contain', margin: '0 auto' }} /> : (c.icon || '🙏')}
              </div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{c.title}</div>
              <div style={{ fontSize: '0.78rem', color: '#888', marginTop: 4, marginBottom: 14 }}>{c.description}</div>

              <div style={{ display: 'flex', justifyContent: 'center', gap: 6, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                <button title="View" onClick={() => setViewing(c)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, width: 30, height: 30, cursor: 'pointer' }}>👁</button>
                <button title="Edit" onClick={() => openEditForm(c)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, width: 30, height: 30, cursor: 'pointer' }}>✏️</button>
                <button title={c.isActive ? 'Move to Draft' : 'Publish'} onClick={() => handleTogglePublish(c)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, width: 30, height: 30, cursor: 'pointer' }}>
                  {c.isActive ? '📦' : '📢'}
                </button>
                <button title="Delete" onClick={() => handleDelete(c._id)} style={{ background: 'none', border: '1px solid #f5c6c6', color: '#c62828', borderRadius: 6, width: 30, height: 30, cursor: 'pointer' }}>🗑</button>
              </div>
            </div>
          ))}
          {cards.length === 0 && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 40, color: '#888' }}>No cards added yet.</div>
          )}
        </div>
      )}

      {viewing && (
        <div className="modal-overlay" onClick={() => setViewing(null)}>
          <div className="modal-card auth-card" style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <button type="button" className="modal-close" onClick={() => setViewing(null)} aria-label="Close">✕</button>
            <div style={{ fontSize: '2.5rem', marginBottom: 10 }}>
              {iconUrl(viewing.icon) ? <img src={iconUrl(viewing.icon)} alt={viewing.title} style={{ width: 56, height: 56, objectFit: 'contain', margin: '0 auto' }} /> : (viewing.icon || '🙏')}
            </div>
            <h3 style={{ fontWeight: 700, marginTop: 6 }}>{viewing.title}</h3>
            <p style={{ marginTop: 10, color: 'var(--text-mid)', fontSize: '0.9rem', lineHeight: 1.6, textAlign: 'left' }}>{viewing.description}</p>
            <span className={`badge ${viewing.isActive ? 'badge-green' : 'badge-orange'}`} style={{ display: 'inline-block', marginTop: 10 }}>
              {viewing.isActive ? 'Published' : 'Draft'}
            </span>
            <div style={{ marginTop: 16, fontSize: '0.8rem', color: '#999' }}>Display order: {viewing.order ?? 0}</div>
          </div>
        </div>
      )}
    </div>
  );
}
